/**
 * Pipe implementation for UI Label Translate.
 * This file provides reusable view-layer transformation logic for templates.
 */
import { ChangeDetectorRef, Pipe, PipeTransform, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { resolveTranslatedUiLabel } from './ui-label-translate.util';

/**
 * PIPE: Ui Label Translate
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Pipe({
  name: 'uiLabelTranslate',
  standalone: true,
  pure: false
})
export class UiLabelTranslatePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    // OnPush wrappers do not automatically re-check when ngx-translate changes
    // language, so mirror TranslatePipe's behavior and mark the host view dirty.
    merge(
      this.translate.onLangChange,
      this.translate.onTranslationChange,
      this.translate.onFallbackLangChange
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.cdr.markForCheck());
  }

  /**
   * BUSINESS LOGIC: transform
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  transform(value: string | null | undefined): string {
    return resolveTranslatedUiLabel(this.translate, value);
  }
}
